// frontend/src/components/dashboard/FarmMap.jsx
import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Extracts normalized [lat, lng] coordinates from plot object
 */
const getPlotCoordinates = (plot) => {
  if (!plot) return null;
  const lat = Number(plot.latitude ?? plot.lat ?? plot.location?.lat ?? plot.coords?.lat);
  const lng = Number(plot.longitude ?? plot.lng ?? plot.location?.lng ?? plot.coords?.lng);

  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
    return [lat, lng];
  }
  return null;
};

/**
 * Extracts device count from plot object
 */
const getPlotDeviceCount = (plot) => {
  if (typeof plot.deviceCount === 'number') return plot.deviceCount;
  if (typeof plot.devicesCount === 'number') return plot.devicesCount;
  if (typeof plot.devices_count === 'number') return plot.devices_count;
  if (Array.isArray(plot.devices)) return plot.devices.length;
  if (Array.isArray(plot.state_data?.devices)) return plot.state_data.devices.length;
  return 0;
};

const FarmMap = ({ plots = [], selectedPlotId, onSelectPlot, onAddDevice }) => {
  const [mapNode, setMapNode] = useState(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (!mapNode) return;

    // Reset previous instance if node changes
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    if (mapNode._leaflet_id) {
      mapNode._leaflet_id = null;
    }

    try {
      const map = L.map(mapNode, {
        zoomControl: true,
        attributionControl: false
      }).setView([0, 0], 2);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        subdomains: 'abcd',
        maxZoom: 19
      }).addTo(map);

      mapInstanceRef.current = map;
      markersRef.current = {};

      const validBounds = [];

      plots.forEach((plot) => {
        const coords = getPlotCoordinates(plot);
        if (!coords) return;

        const plotId = plot.site_id || plot.id || plot.plotId || plot.external_id;
        const isSelected = String(selectedPlotId) === String(plotId);
        const riskLevel = String(plot.overallRiskLevel || plot.riskLevel || plot.risk_level || 'LOW').toLowerCase();
        const physicalCount = getPlotDeviceCount(plot);
        const plotName = plot.name || 'Plot';

        // Badge Text: Differentiate Regional Proxy vs Physical Hardware
        const badgeLabel = physicalCount > 0
          ? `📡 ${physicalCount} Sensor${physicalCount > 1 ? 's' : ''}`
          : `🌐 Regional (0 Physical)`;

        const customIcon = L.divIcon({
          className: 'custom-plot-marker-wrapper',
          html: `
            <div class="plot-map-marker risk-${riskLevel} ${isSelected ? 'marker-selected' : ''}">
              <div class="marker-badge" title="${physicalCount} physical sensors active">
                ${badgeLabel}
              </div>
              <div class="marker-label">${plotName}</div>
            </div>
          `,
          iconSize: [0, 0],
          iconAnchor: [0, 0]
        });

        const marker = L.marker(coords, { icon: customIcon }).addTo(map);

        // Bind interactive Leaflet popup with two explicit choices
        const popupContent = document.createElement('div');
        popupContent.className = 'farm-map-popup';
        popupContent.innerHTML = `
          <div class="popup-title"><strong>${plotName}</strong></div>
          <div class="popup-status">
            <div><small>Status:</small> <span class="status-pill active">🌐 Regional Feed Active</span></div>
            <div><small>Hardware:</small> <strong>${physicalCount} Physical Sensors</strong></div>
          </div>
          <div class="popup-actions">
            <button class="popup-btn btn-view" id="btn-view-${plotId}">View Site Details</button>
            <button class="popup-btn btn-add" id="btn-add-${plotId}">+ Add Hardware</button>
          </div>
        `;

        marker.bindPopup(popupContent, { minWidth: 200 });

        // Bind popup button listeners when opened
        marker.on('popupopen', () => {
          const viewBtn = document.getElementById(`btn-view-${plotId}`);
          const addBtn = document.getElementById(`btn-add-${plotId}`);

          if (viewBtn) {
            viewBtn.onclick = () => {
              if (onSelectPlot) onSelectPlot(plotId);
              map.closePopup();
            };
          }

          if (addBtn) {
            addBtn.onclick = () => {
              if (onAddDevice) onAddDevice(plot);
              map.closePopup();
            };
          }
        });

        markersRef.current[plotId] = marker;
        validBounds.push(coords);
      });

      // Zoom focus logic
      const selectedPlot = plots.find(
        (p) => String(p.site_id || p.id || p.plotId || p.external_id) === String(selectedPlotId)
      );
      const selectedCoords = selectedPlot ? getPlotCoordinates(selectedPlot) : null;

      if (selectedCoords) {
        map.setView(selectedCoords, 14);
      } else if (validBounds.length === 1) {
        map.setView(validBounds[0], 14);
      } else if (validBounds.length > 1) {
        map.fitBounds(validBounds, { padding: [40, 40], maxZoom: 16 });
      } else {
        map.setView([20, 0], 2);
      }

      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 150);

    } catch (err) {
      console.error('[FarmMap Init Error]:', err);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersRef.current = {};
      }
    };
  }, [mapNode, plots, selectedPlotId, onSelectPlot, onAddDevice]);

  return (
    <div className="farm-map-wrapper">
      <div className="farm-map-header">
        <h4>Plot Locations</h4>
      </div>
      <div ref={setMapNode} className="farm-map-container" style={{ minHeight: '340px', width: '100%' }} />
    </div>
  );
};

export default FarmMap;
